import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { get, post } from '../api';
import { useAuth } from '../auth';
import { Badge, ErrorBox, Modal, PageTitle, Spinner } from '../components/ui';

type Counter = { created: number; updated: number; skipped: number };
interface Report {
  shop: { externalId: string; name: string; action: string; slug: string | null };
  pages: Counter;
  slides: number;
  deliveryAddresses: number;
  domains: number;
  categories: Counter;
  groups: Counter;
  vendors: Counter;
  products: Counter & { variantsCreated: number; variantsUpdated: number; images: number; offerImages?: number; videos?: number; bundles?: number; inAssortment: number };
  customers: Counter;
  discounts: Counter;
  orders: { linked: number; missing: number; cancelled?: number };
  warnings: string[];
  durationMs: number;
}
interface Run { id: string; shopExternalId: string; shopId: string | null; mode: 'dry_run' | 'live'; status: string; report: Report | Record<string, never>; error: string | null; createdByName: string; createdAt: string; finishedAt: string | null }
interface Candidate { id: number; title: string; type: string | null; is_active: boolean; published_at: string | null; unpublished_at: string | null; orders: number; logins: number; products: number; domains: string[]; shopId: string | null; slug: string | null; switchedAt: string | null; lastRun: Run | null; importedAt: string | null }

const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleString('da-DK', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '–');
const c = (x: Counter) => `${x.created} nye · ${x.updated} opdateret${x.skipped ? ` · ${x.skipped} sprunget over` : ''}`;

/** P2.6: move shops from 3x here (import with a dry run first), then switch each one. */
export function X3ImportPage() {
  const qc = useQueryClient();
  const { can } = useAuth();
  const [months, setMonths] = useState(6);
  const [error, setError] = useState<unknown>(null);
  const [show, setShow] = useState<Run | null>(null);
  const list = useQuery({ queryKey: ['x3-import', months], queryFn: () => get<Candidate[]>(`/x3-import/shops?months=${months}`) });
  const running = list.data?.some((s) => s.lastRun && ['queued', 'running'].includes(s.lastRun.status));
  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => void qc.invalidateQueries({ queryKey: ['x3-import'] }), 2000);
    return () => clearInterval(t);
  }, [running, qc]);
  const start = useMutation({ mutationFn: (v: { id: number; mode: 'dry_run' | 'live' }) => post<Run>(`/x3-import/shops/${v.id}/run`, { mode: v.mode }), onSuccess: () => { setError(null); void qc.invalidateQueries({ queryKey: ['x3-import'] }); }, onError: setError });
  const sw = useMutation({ mutationFn: (v: { shopId: string; on: boolean }) => post(`/x3-import/switch/${v.shopId}`, { on: v.on }), onSuccess: () => { setError(null); void qc.invalidateQueries({ queryKey: ['x3-import'] }); }, onError: setError });
  const edit = can('settings.manage');

  return (
    <div className="space-y-4">
      <PageTitle
        title="Flyt shops fra 3x"
        subtitle="Kør først en prøve (intet gemmes), så importen, og skift til sidst shoppen over. Importen kan køres igen; alt matches på 3x' id'er."
        actions={
          <select className="input w-56" value={months} onChange={(e) => setMonths(Number(e.target.value))}>
            {[3, 6, 12, 24].map((m) => <option key={m} value={m}>Aktive seneste {m} mdr.</option>)}
          </select>
        }
      />
      <ErrorBox error={error ?? list.error} />
      {list.isLoading ? <Spinner /> : (
        <div className="card p-0">
          <table className="table text-sm">
            <thead><tr><th>3x-shop</th><th>Åben</th><th className="text-right">Ordrer</th><th className="text-right">Logins</th><th className="text-right">Produkter</th><th>Domæner</th><th>Seneste kørsel</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {list.data?.map((s) => {
                const r = s.lastRun;
                const busy = r && ['queued', 'running'].includes(r.status);
                return (
                  <tr key={s.id}>
                    <td>
                      <div className="font-medium">{s.title}</div>
                      <div className="text-xs text-muted">3x {s.id} · {s.type ?? ''}{s.shopId && <> · <Link to="/shops/$id/storefront" params={{ id: s.shopId }} className="text-primary hover:underline">webshop her</Link></>}</div>
                    </td>
                    <td className="text-xs">{s.published_at?.slice(0, 10) ?? '?'} → {s.unpublished_at?.slice(0, 10) ?? '?'}</td>
                    <td className="text-right tabular-nums">{s.orders}</td>
                    <td className="text-right tabular-nums">{s.logins}</td>
                    <td className="text-right tabular-nums">{s.products}</td>
                    <td className="text-xs">{s.domains.join(', ')}</td>
                    <td className="text-xs">
                      {r ? (
                        <button className="text-left hover:underline" onClick={() => setShow(r)}>
                          {r.mode === 'live' ? 'Import' : 'Prøve'} · {r.status === 'done' ? fmt(r.finishedAt) : r.status}{r.error ? ` · ${r.error.slice(0, 60)}` : ''}
                        </button>
                      ) : '–'}
                    </td>
                    <td>
                      {s.switchedAt ? <Badge tone="green">skiftet {fmt(s.switchedAt).slice(0, 8)}</Badge> : s.importedAt ? <Badge tone="blue">importeret</Badge> : <Badge>i 3x</Badge>}
                    </td>
                    <td className="whitespace-nowrap">
                      {edit && (
                        <span className="flex gap-1">
                          <button className="btn" disabled={!!busy} onClick={() => start.mutate({ id: s.id, mode: 'dry_run' })}>Prøv</button>
                          <button className="btn" disabled={!!busy} onClick={() => confirm(`Importér ${s.title} fra 3x?`) && start.mutate({ id: s.id, mode: 'live' })}>Importér</button>
                          {s.shopId && s.importedAt && !s.switchedAt && <button className="btn btn-primary" onClick={() => confirm(`Skift ${s.title} til den nye webshop? Bestilling åbner her, og 3x-ordrer herefter markeres til tjek. Peg domænet hertil og luk shoppen i 3x bagefter.`) && sw.mutate({ shopId: s.shopId!, on: true })}>Skift</button>}
                          {s.shopId && s.switchedAt && <button className="btn" onClick={() => confirm('Skift tilbage til 3x?') && sw.mutate({ shopId: s.shopId!, on: false })}>Fortryd</button>}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {list.data?.length === 0 && <tr><td colSpan={9} className="text-muted">Ingen shops fra 3x i perioden</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      <div className="card text-sm text-muted">
        <div className="label">Skiftet, trin for trin</div>
        <ol className="list-decimal space-y-1 pl-5">
          <li>Prøv: viser hvad importen vil gøre. Ret advarsler i 3x eller her.</li>
          <li>Importér: shop, sider, kategorier, produkter med priser og billeder, logins (adgangskoderne virker uændret), rabatter, og QR-koderne på årets ordrer.</li>
          <li>Gennemgå webshoppen (Forhåndsvis) og sæt Bambora og fragtpriser op.</li>
          <li>Skift: bestilling åbner her. Peg domænet på serveren (CNAME til thebb.samsinnovation.dev) og udsted certifikat; luk shoppen i 3x.</li>
        </ol>
      </div>
      {show && <ReportModal run={show} onClose={() => setShow(null)} />}
    </div>
  );
}

function ReportModal({ run, onClose }: { run: Run; onClose: () => void }) {
  const r = run.report as Report;
  return (
    <Modal title={`${run.mode === 'live' ? 'Import' : 'Prøvekørsel'}: ${r.shop?.name ?? run.shopExternalId}`} onClose={onClose}>
      <div className="max-h-[70vh] space-y-2 overflow-y-auto text-sm">
        <div className="text-muted">{run.createdByName} · {fmt(run.createdAt)} · {run.status}{r.durationMs ? ` · ${(r.durationMs / 1000).toFixed(1)} s` : ''}</div>
        {run.error && <div className="rounded bg-red-50 p-2 text-red-800">{run.error}</div>}
        {r.shop && (
          <table className="w-full">
            <tbody>
              <tr><td className="pr-4 text-muted">Shop</td><td>{r.shop.action === 'create' ? 'oprettes' : 'opdateres'} · /s/{r.shop.slug}</td></tr>
              <tr><td className="pr-4 text-muted">Sider</td><td>{c(r.pages)}</td></tr>
              <tr><td className="pr-4 text-muted">Slides · adresser · domæner</td><td>{r.slides} · {r.deliveryAddresses} · {r.domains}</td></tr>
              <tr><td className="pr-4 text-muted">Kategorier</td><td>{c(r.categories)}</td></tr>
              <tr><td className="pr-4 text-muted">Kundegrupper</td><td>{c(r.groups)}</td></tr>
              <tr><td className="pr-4 text-muted">Leverandører</td><td>{c(r.vendors)}</td></tr>
              <tr><td className="pr-4 text-muted">Produkter</td><td>{c(r.products)} · varianter {r.products.variantsCreated} nye / {r.products.variantsUpdated} opdateret · {r.products.inAssortment} i sortimentet · {r.products.images} billeder{r.products.offerImages ? ` (heraf ${r.products.offerImages} tilbudsbilleder)` : ''}{r.products.videos ? ` · ${r.products.videos} videoer` : ''}{r.products.bundles ? ` · ${r.products.bundles} sampak` : ''}</td></tr>
              <tr><td className="pr-4 text-muted">Kunder (logins)</td><td>{c(r.customers)}</td></tr>
              <tr><td className="pr-4 text-muted">Rabatter</td><td>{c(r.discounts)}</td></tr>
              <tr><td className="pr-4 text-muted">Årets ordrer</td><td>{r.orders.linked} koblet (QR og kunde){r.orders.missing ? ` · ${r.orders.missing} mangler her` : ''}{r.orders.cancelled ? ` · ${r.orders.cancelled} annulleret i 3x (hentes ikke)` : ''}</td></tr>
            </tbody>
          </table>
        )}
        {r.warnings?.length > 0 && (
          <div className="rounded bg-amber-50 p-2 text-amber-900">
            <div className="label">Advarsler</div>
            {r.warnings.map((w, i) => <div key={i}>{w}</div>)}
          </div>
        )}
      </div>
    </Modal>
  );
}
